/**
 * Campaign System - CountdownTimer Component
 * Componente de contador regressivo modular e reutilizável
 */

import { useState, useEffect } from 'react';
import type { Contador, CountdownTimerProps, TimeRemaining } from './types';
import { getContadores, getTimeRemaining, getTemaClasses } from './utils';

const DEFAULT_STORAGE_KEY = 'campaign_contadores';

export default function CountdownTimer({
  location,
  storageKey = DEFAULT_STORAGE_KEY,
}: CountdownTimerProps) {
  const [contador, setContador] = useState<Contador | null>(null);
  const [timeLeft, setTimeLeft] = useState<TimeRemaining>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    expired: false,
  });

  useEffect(() => {
    const loadContador = async () => {
      try {
        const contadores = await getContadores(storageKey);

        const active = contadores.find((c) => {
          return c.location === location && c.ativo;
        });

        setContador(active || null);
      } catch (error) {
        console.error('Erro ao carregar contador:', error);
        setContador(null);
      }
    };

    loadContador();
  }, [location, storageKey]);

  useEffect(() => {
    if (!contador) return;

    const updateTime = () => {
      const dataFim = contador.dataFim.toISOString();
      const modo = contador.modoRecorrencia || 'sem-recorrencia';
      const remaining = getTimeRemaining(dataFim, modo);
      setTimeLeft(remaining);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, [contador]);

  if (!contador || timeLeft.expired) return null;

  const theme = getTemaClasses(contador.tema);
  const titulo = contador.titulo || '';
  const label = 'OFERTA LIMITADA';

  return (
    <div className="flex justify-center my-6">
      <div
        className={`inline-flex flex-col items-center gap-3 px-6 py-4 rounded-2xl border-2 ${theme.container}`}
      >
        <p className={`text-xs font-bold uppercase tracking-wider ${theme.text}`}>
          {label}
        </p>

        <div className="flex items-center gap-2">
          {timeLeft.days > 0 && (
            <div className="flex flex-col items-center">
              <div
                className={`w-14 h-14 flex items-center justify-center rounded-lg border-2 text-xl font-bold ${theme.block}`}
              >
                {String(timeLeft.days).padStart(2, '0')}
              </div>
              <span className={`text-xs font-semibold mt-1.5 ${theme.text}`}>Dias</span>
            </div>
          )}

          <div className="flex flex-col items-center">
            <div
              className={`w-14 h-14 flex items-center justify-center rounded-lg border-2 text-xl font-bold ${theme.block}`}
            >
              {String(timeLeft.hours).padStart(2, '0')}
            </div>
            <span className={`text-xs font-semibold mt-1.5 ${theme.text}`}>Horas</span>
          </div>

          <div className="flex flex-col items-center">
            <div
              className={`w-14 h-14 flex items-center justify-center rounded-lg border-2 text-xl font-bold ${theme.block}`}
            >
              {String(timeLeft.minutes).padStart(2, '0')}
            </div>
            <span className={`text-xs font-semibold mt-1.5 ${theme.text}`}>Min</span>
          </div>

          <div className="flex flex-col items-center">
            <div
              className={`w-14 h-14 flex items-center justify-center rounded-lg border-2 text-xl font-bold ${theme.block}`}
            >
              {String(timeLeft.seconds).padStart(2, '0')}
            </div>
            <span className={`text-xs font-semibold mt-1.5 ${theme.text}`}>Seg</span>
          </div>
        </div>

        {titulo && <p className={`text-sm font-bold text-center ${theme.text}`}>{titulo}</p>}
      </div>
    </div>
  );
}